import * as React from 'react';
import { merge } from '../../../helpers/Utility';
import { Loader } from '../Loader';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  compact?: boolean;
  big?: boolean;
  primary?: boolean;
  secondary?: boolean;
  danger?: boolean;
  tint?: boolean;
  loading?: boolean;
}

export enum ButtonVariants {
  disabled = 'bg-gray-200 border border-gray-400 text-primary-std font-medium focus:ring-primary-500',
  primary = 'bg-primary-500 hover:bg-primary-600 border border-primary-600 text-white font-medium focus:ring-primary-500',
  secondary = 'bg-secondary-500 hover:bg-secondary-600 border border-secondary-600 text-white font-medium focus:ring-secondary-500',
  danger = 'bg-red-500 hover:bg-red-600 border border-red-600 text-white font-medium focus:ring-red-500',
  primaryTint = 'bg-primary-50 hover:bg-primary-100 border border-primary-200 dark:text-primary-400 text-primary-700 font-normal focus:ring-primary-500',
  secondaryTint = 'bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 dark:text-secondary-400 text-secondary-700 font-normal focus:ring-secondary-500',
  dangerTint = 'bg-red-50 hover:bg-red-100 dark:hover:bg-red-600 dark:hover:bg-opacity-30 dark:bg-red-600 dark:bg-opacity-20 border border-red-200 dark:border-red-300 text-red-600 dark:text-red-300 font-medium focus:ring-red-500',
  default = 'bg-primary-std border border-primary-std hover:bg-secondary-std text-primary-std font-normal focus:ring-gray-600 dark:focus:ring-gray-400'
}

export const Button: React.FC<ButtonProps> = ({
  children,
  compact,
  big,
  primary,
  secondary,
  loading,
  danger,
  disabled,
  tint,
  className,
  type = 'button',
  ...props
}) => {
  const preventsInteraction = disabled || loading;

  const style = (() => {
    if (disabled) return ButtonVariants.disabled;
    if (danger && !tint) return ButtonVariants.danger;
    if (primary && !tint) return ButtonVariants.primary;
    if (secondary && !tint) return ButtonVariants.secondary;
    if (primary && tint) return ButtonVariants.primaryTint;
    if (secondary && tint) return ButtonVariants.secondaryTint;
    if (danger && tint) return ButtonVariants.dangerTint;

    return ButtonVariants.default;
  })();

  return (
    <button
      type={type}
      disabled={preventsInteraction}
      className={merge(
        preventsInteraction ? 'pointer-events-none' : '',
        !compact ? 'px-3 py-2 rounded-md' : 'px-2 py-1.5 rounded-md',
        big ? `px-4 py-4` : 'px-3 py-2 rounded-md',
        'shadow-sm text-sm text-center relative overflow-hidden',
        'ring-offset-white dark:ring-offset-black focus:outline-none focus:ring-2 focus:ring-offset-2 shrink-0',
        style,
        className
      )}
      {...props}
    >
      {loading ? <Loader className="w-6 h-6" /> : children}
    </button>
  );
};
